import {
  COOKIE_TRANSACTION_ID,
  DEFAULT_CONTEXT_TIMEOUT,
  DEFAULT_IDLE_TIMEOUT,
  DEFAULT_OUTLINK_SELECTOR,
  MAX_SEQUENCE_LENGTH,
  SESSION_PARAM,
} from './constants';
import {
  resolveGeoFromCookie,
  resolveGeoFromLegacyGlobal,
  resolveIpFromLegacyGlobal,
  resolveVisitorContext,
} from './context-resolver';
import {
  clearSession,
  deleteLegacySessionIdCookie,
  getLegacySessionIdFromCookie,
  getSessionIdFromCookie,
  getSessionNewMarkerFromCookie,
  getTransactionIdFromCookie,
  getURLParameter,
  getVisitorIdFromCookie,
  loadSession,
  saveSession,
  setSessionIdCookie,
  setTransactionIdCookie,
  setVisitorIdFallbackCookie,
} from './storage';
import { ActivityTracker } from './activity-tracker';
import { LinkDecorator } from './link-decorator';
import { vlog } from './verbose';
import { parseUaContext, type UaContext } from './ua-context';
import type {
  DecisionSessionPayload,
  PSSessionManager,
  SessionContext,
  SessionGeo,
  SessionManagerConfig,
  StoredSession,
} from './types';

const SESSION_ID_RE =
  /^[0-9a-f]{32}$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// UUIDv7 in the canonical hyphenated form (RFC 9562, 36 chars) — PS-553 reverses
// PS-486's undashed rendering for consistency with event_id/decision_id. Wave 1
// widened SESSION_ID_RE so both forms validate; legacy 32-hex ids resume without
// re-mint. The 48-bit ms prefix makes ids time-ordered. Hand-rolled:
// crypto.randomUUID() is v4, no timestamp. Rendering must stay byte-identical with
// the edge minter and ELC's uuidv7Canonical() (not randomHexId — jid stays random
// per AC5); shared conformance vectors in PS-509.
function generateId(): string {
  const bytes = new Uint8Array(16);
  const view = new DataView(bytes.buffer);
  const ms = Date.now(); // 48-bit ms, big-endian; es2017-safe (no BigInt)
  view.setUint16(0, Math.floor(ms / 0x100000000));
  view.setUint32(2, ms >>> 0);
  crypto.getRandomValues(bytes.subarray(6));
  bytes[6] = (bytes[6] & 0x0f) | 0x70; // version 7
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function isValidSessionId(id: string | null | undefined): id is string {
  return SESSION_ID_RE.test(id || '');
}

function nowISO(): string {
  return new Date().toISOString();
}

/**
 * SessionManager owns the full session lifecycle:
 * creation, resumption, idle expiration, activity tracking,
 * cross-domain link decoration, and exposing a shared context API.
 */
export class SessionManager implements PSSessionManager {
  readonly #config: SessionManagerConfig;
  #stored: StoredSession;
  #isNew: boolean;
  #visitorId: string | null;
  #activityTracker: ActivityTracker;
  #linkDecorator: LinkDecorator;
  #expiredCallbacks: Array<() => void> = [];
  #ipAddress: string | null = null;
  #geo: SessionGeo | null = null;
  #ua: UaContext;
  #contextReady = false;

  constructor(config: Partial<SessionManagerConfig> = {}) {
    this.#config = {
      idleTimeout: config.idleTimeout ?? DEFAULT_IDLE_TIMEOUT,
      outlinkSelector: config.outlinkSelector ?? DEFAULT_OUTLINK_SELECTOR,
      sessionParam: config.sessionParam ?? SESSION_PARAM,
      cookieDomain: config.cookieDomain,
      geoEndpoint: config.geoEndpoint,
      contextTimeout: config.contextTimeout ?? DEFAULT_CONTEXT_TIMEOUT,
    };

    const resolved = this.#resolveSession();
    this.#stored = resolved.session;
    this.#isNew = resolved.isNew;
    this.#visitorId = this.#resolveVisitorId();

    // Single synchronous UA parse — the one source for browser/os/device
    // across all PS consumers (ADR-0032).
    this.#ua = parseUaContext(navigator.userAgent || '');

    // Sync geo resolution — precedence: PS_GEO > mf_geo cookie > window.MF.* (legacy CMS)
    const ssrGeo = (window as any).PS_GEO;
    if (ssrGeo && typeof ssrGeo === 'object' && typeof ssrGeo.country === 'string') {
      this.#geo = ssrGeo as SessionGeo;
      this.#contextReady = true;
      vlog('geo: resolved from window.PS_GEO (SSR)', this.#geo);
    } else {
      const cookieGeo = resolveGeoFromCookie();
      if (cookieGeo) {
        this.#geo = cookieGeo.geo;
        this.#ipAddress = cookieGeo.ipAddress;
        this.#contextReady = true;
        vlog('geo: resolved from mf_geo cookie', { ipAddress: this.#ipAddress, geo: this.#geo });
      }
    }

    // Last sync fallback: legacy cms-macros globals (window.MF.geoData + window.MF.ipAddress).
    // If geo is resolved here, mark contextReady so the async geoEndpoint does not fire.
    // If only IP is resolved, leave contextReady false so async can still populate geo.
    if (!this.#geo) {
      const legacyGeo = resolveGeoFromLegacyGlobal();
      if (legacyGeo) {
        this.#geo = legacyGeo;
        this.#contextReady = true;
        vlog('geo: resolved from legacy window.MF.geoData', this.#geo);
      }
    }
    if (!this.#ipAddress) {
      this.#ipAddress = resolveIpFromLegacyGlobal();
      if (this.#ipAddress) vlog('ip: resolved from legacy window.MF.ipAddress', this.#ipAddress);
    }

    vlog('context resolved (sync)', {
      sessionId: this.#stored.id,
      isNew: this.#isNew,
      visitorId: this.#visitorId,
      transactionId: this.#stored.transactionId,
      ipAddress: this.#ipAddress,
      geo: this.#geo,
      contextReady: this.#contextReady,
    });

    // Persist immediately
    this.#persist();

    // Legacy migration: expire mfSessionId only after mf_sid is written
    if (resolved.migratedFromLegacy) {
      deleteLegacySessionIdCookie();
      vlog('session: expired legacy mfSessionId cookie after migration');
    }

    // Record current page in sequence
    this.recordPageVisit(window.location.href);

    // Activity tracking
    this.#activityTracker = new ActivityTracker(() => this.touch());
    this.#activityTracker.start();

    // Link decoration
    this.#linkDecorator = new LinkDecorator(
      this.#config.sessionParam,
      this.#stored.id,
      this.#config.outlinkSelector,
    );

    // Fire-and-forget: resolve IP + geo context asynchronously
    this.#resolveContextAsync();
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  get contextReady(): boolean {
    return this.#contextReady;
  }

  get context(): SessionContext {
    return {
      id: this.#stored.id,
      startedAt: this.#stored.startedAt,
      lastActivityAt: this.#stored.lastActivityAt,
      initialUrl: this.#stored.initialUrl,
      currentUrl: window.location.href,
      referrerUrl: this.#stored.referrerUrl,
      pageSequence: [...this.#stored.pageSequence],
      eventSequence: [...this.#stored.eventSequence],
      isNew: this.#isNew,
      transactionId: this.#stored.transactionId,
      visitorId: this.#visitorId,
      ipAddress: this.#ipAddress,
      geo: this.#geo,
      browser: this.#ua.browser,
      os: this.#ua.os,
      device: this.#ua.device,
    };
  }

  recordPageVisit(url?: string): void {
    const page = url ?? window.location.href;
    if (this.#stored.pageSequence.length < MAX_SEQUENCE_LENGTH) {
      this.#stored.pageSequence.push(page);
    }
    this.touch();
  }

  recordEvent(eventName: string): void {
    if (this.#stored.eventSequence.length < MAX_SEQUENCE_LENGTH) {
      this.#stored.eventSequence.push(eventName);
    }
    this.touch();
  }

  touch(): void {
    this.#stored.lastActivityAt = nowISO();
    this.#persist();
  }

  toDecisionPayload(
    pageId: string,
    pageListId: string,
  ): DecisionSessionPayload & { page_id: string; page_list_id: string } {
    return {
      session_id: this.#stored.id,
      started_at: this.#stored.startedAt,
      last_activity_at: this.#stored.lastActivityAt,
      initial_url: this.#stored.initialUrl,
      current_url: window.location.href,
      referrer_url: this.#stored.referrerUrl,
      page_sequence: [...this.#stored.pageSequence],
      event_sequence: [...this.#stored.eventSequence],
      requested_at: nowISO(),
      page_id: pageId,
      page_list_id: pageListId,
    };
  }

  onExpired(cb: () => void): void {
    this.#expiredCallbacks.push(cb);
  }

  /** Decorate outlinks and start observing for new ones. Call after DOM is ready. */
  initLinks(): void {
    this.#linkDecorator.decorateAll();
    this.#linkDecorator.observe();
  }

  // -------------------------------------------------------------------------
  // Internal
  // -------------------------------------------------------------------------

  /**
   * Resolve IP + geo context asynchronously. Sets contextReady = true and
   * dispatches ps:context-ready even on failure (with null values).
   * Called fire-and-forget from the constructor.
   */
  async #resolveContextAsync(): Promise<void> {
    try {
      if (!this.#contextReady && this.#config.geoEndpoint) {
        vlog('context async: fetching IP + geo from endpoint', this.#config.geoEndpoint);
        const result = await resolveVisitorContext(
          this.#config.geoEndpoint,
          this.#config.contextTimeout,
        );
        this.#ipAddress = result.ipAddress;
        this.#geo = result.geo;
        vlog('context async: resolved', { ipAddress: this.#ipAddress, geo: this.#geo });
      } else {
        // Yield to ensure the event fires after the constructor returns
        // and window.PS.session is assigned (consumers depend on it).
        await Promise.resolve();
      }
    } catch {
      // Ensure we always proceed — nulls are acceptable
    } finally {
      this.#contextReady = true;
      vlog('context ready', { ipAddress: this.#ipAddress, geo: this.#geo });
      window.dispatchEvent(new CustomEvent('ps:context-ready'));
    }
  }

  /**
   * Resolve the session. Precedence: URL param handoff > edge-minted mf_sid
   * cookie > legacy mfSessionId migration > new session. Handles idle expiration.
   */
  #resolveSession(): { session: StoredSession; isNew: boolean; migratedFromLegacy?: boolean } {
    // 1. URL param (cross-domain handoff) — continuity wins over an edge-minted
    //    sid; #persist overrides mf_sid with the handed-off id. A stale
    //    mf_sid_new minted for the overridden sid resolves false everywhere
    //    (value won't match mf_sid), so it is left alone.
    const fromUrl = getURLParameter(this.#config.sessionParam);
    if (fromUrl && isValidSessionId(fromUrl)) {
      const stored = loadSession();
      if (stored && stored.id === fromUrl && !this.#isExpired(stored)) {
        stored.lastActivityAt = nowISO();
        vlog('session: resumed from URL param handoff', { sessionId: stored.id });
        return { session: stored, isNew: false };
      }
      // Continuation of a session started on another domain, not a new one.
      vlog('session: adopted handed-off session from URL param', { sessionId: fromUrl });
      return { session: this.#createSession(fromUrl), isNew: false };
    }

    // 2. mf_sid cookie (edge-covered page): the edge id always wins. isNew comes
    //    from the one-shot mf_sid_new marker, never from local state.
    const cookieId = getSessionIdFromCookie();
    if (cookieId && isValidSessionId(cookieId)) {
      const isNew = getSessionNewMarkerFromCookie() === cookieId;
      const stored = loadSession();
      if (stored && stored.id === cookieId && !this.#isExpired(stored)) {
        stored.lastActivityAt = nowISO();
        vlog('session: resumed edge mf_sid with stored state', { sessionId: stored.id, isNew });
        return { session: stored, isNew };
      }
      // localStorage missing, mismatched, or idle-expired — adopt the edge id
      // with fresh state rather than minting a different one.
      vlog('session: adopted edge mf_sid, fresh state', { sessionId: cookieId, isNew });
      return { session: this.#createSession(cookieId), isNew };
    }

    // 3. No mf_sid (page not covered by the edge): migrate an in-flight legacy
    //    mfSessionId session so it survives the rename.
    const legacyId = getLegacySessionIdFromCookie();
    if (legacyId && isValidSessionId(legacyId)) {
      const stored = loadSession();
      if (stored && stored.id === legacyId && !this.#isExpired(stored)) {
        stored.lastActivityAt = nowISO();
        vlog('session: migrated legacy mfSessionId session', { sessionId: stored.id });
        return { session: stored, isNew: false, migratedFromLegacy: true };
      }
    }

    // 4. Create new session
    const created = this.#createSession();
    vlog('session: created new session', { sessionId: created.id });
    return { session: created, isNew: true };
  }

  #createSession(id?: string): StoredSession {
    const now = nowISO();
    const transactionId = this.#resolveTransactionId();

    // Fire expired callbacks if replacing an existing session
    const old = loadSession();
    if (old) {
      clearSession();
      for (const cb of this.#expiredCallbacks) {
        try { cb(); } catch { /* ignore */ }
      }
    }

    return {
      id: id ?? generateId(),
      startedAt: now,
      lastActivityAt: now,
      initialUrl: window.location.href,
      referrerUrl: document.referrer,
      pageSequence: [],
      eventSequence: [],
      transactionId,
    };
  }

  /**
   * Resolve visitor ID per ADR-0015. Normally the mf_vid cookie is server-set
   * by a CloudFront Function (first-party, 1yr TTL). If not present (non-owned
   * domain), fall back to a JS-set cookie (Safari ITP caps to 7 days).
   */
  #resolveVisitorId(): string | null {
    const existing = getVisitorIdFromCookie();
    if (existing) {
      vlog('visitor: resolved from mf_vid cookie', { visitorId: existing });
      return existing;
    }

    // Fallback: generate client-side and set as JS cookie
    const id = generateId();
    setVisitorIdFallbackCookie(id, this.#config.cookieDomain);
    vlog('visitor: no cookie — generated JS fallback', { visitorId: id });
    return id;
  }

  #resolveTransactionId(): string | null {
    const fromCookie = getTransactionIdFromCookie();
    if (fromCookie) return fromCookie;

    const fromUrl = getURLParameter(COOKIE_TRANSACTION_ID);
    if (fromUrl) {
      setTransactionIdCookie(fromUrl, this.#config.idleTimeout, this.#config.cookieDomain);
      return fromUrl;
    }
    return null;
  }

  #isExpired(stored: StoredSession): boolean {
    const lastActivity = new Date(stored.lastActivityAt).getTime();
    if (isNaN(lastActivity)) return true;
    const elapsed = (Date.now() - lastActivity) / 1000;
    return elapsed > this.#config.idleTimeout;
  }

  #persist(): void {
    saveSession(this.#stored);
    setSessionIdCookie(this.#stored.id, this.#config.idleTimeout);
  }
}
